import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { BackendService } from './general/backend.service';
import {
  SurveyReport,
  SurveyReportAiType,
  SurveyReportAnalysisStatus,
  SurveyReportMode,
  SurveyReportOptions,
  SurveyReportRevisionMode,
  SurveyReportRevisionStatus,
} from '../models/survey-report.model';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class SurveyReportService {
  constructor(private backend: BackendService) {}

  analysisStatus(
    surveyId: number | string,
    type: SurveyReportAiType,
    success_callback: (data: SurveyReportAnalysisStatus) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.get(`surveys/${surveyId}/report/analysis-status`, new HttpParams().set('type', type)).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  revisionStatus(
    surveyId: number | string,
    success_callback: (data: SurveyReportRevisionStatus) => void = () => {},
    error_callback: (error: any) => void = () => {},
    useFinalDecisions = false,
  ) {
    this.backend.get(`surveys/${surveyId}/report/revision-status`, new HttpParams().set('use_final_decisions', useFinalDecisions ? '1' : '0')).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  latest(
    surveyId: number | string,
    success_callback: (report: SurveyReport | null) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.get(`surveys/${surveyId}/report/latest`).subscribe(
      (result: any) => success_callback(result?.report ?? null),
      (error) => error_callback(error),
    );
  }

  history(
    surveyId: number | string,
    success_callback: (reports: SurveyReport[]) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.get(`surveys/${surveyId}/report/history`).subscribe(
      (result: any) => success_callback(result?.reports ?? []),
      (error) => error_callback(error),
    );
  }

  show(
    surveyId: number | string,
    reportId: number,
    success_callback: (report: SurveyReport | null) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.get(`surveys/${surveyId}/report/${reportId}`).subscribe(
      (result: any) => success_callback(result?.report ?? null),
      (error) => error_callback(error),
    );
  }

  start(
    surveyId: number | string,
    mode: SurveyReportMode,
    questionIds: number[] = [],
    options: SurveyReportOptions | null = null,
    revisionMode: SurveyReportRevisionMode = 'now',
    revisionQuestionIds: number[] = [],
    success_callback: (report: SurveyReport) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend
      .post(`surveys/${surveyId}/report`, {
        mode,
        question_ids: questionIds,
        options,
        revision_mode: revisionMode,
        revision_question_ids: revisionQuestionIds,
      })
      .subscribe(
        (result: any) => success_callback(result?.report),
        (error) => {
          this.backend.showErrors(error);
          error_callback(error);
        },
      );
  }

  cancel(
    surveyId: number | string,
    reportId: number,
    success_callback: (report: SurveyReport) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.post(`surveys/${surveyId}/report/${reportId}/cancel`, {}).subscribe(
      (result: any) => success_callback(result?.report),
      (error) => {
        if (error?.status === 409 && error?.error?.report) {
          success_callback(error.error.report);
          return;
        }
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  download(
    surveyId: number | string,
    report: SurveyReport,
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.getBlob(`surveys/${surveyId}/report/${report.id}/download`).subscribe(
      (result: any) => saveAs(result.body ?? result, report.file_name || `survey-report-${surveyId}.pptx`),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }
}
